import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  faEdit,
  faEye,
  faEyeSlash,
  faUpload,
  faGripVertical,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Page from '@/components/Page';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Chip from '@/components/Chip';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import { DocumentsAPI } from '@/services/documentsAPI';
import { Document } from '@/types/documents';
import ROUTES from '@/routes/routes';
import UploadVersionModal from './UploadVersionModal';

const TAX_TYPE_LABELS: Record<string, string> = {
  all: 'All Workers',
  w2: 'W-2 Only',
  '1099': '1099 Only',
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch {
    return dateString;
  }
};

interface SortableRowProps {
  doc: Document;
  sortMode: boolean;
  canActivate: (doc: Document) => boolean;
  onToggleActive: (doc: Document) => void;
  onEdit: (doc: Document) => void;
  onUpload: (doc: Document) => void;
  onNavigateToShow: (doc: Document) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  doc,
  sortMode,
  canActivate,
  onToggleActive,
  onEdit,
  onUpload,
  onNavigateToShow,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: doc.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderBottom: '1px solid #eee',
    ...(isDragging
      ? {
          opacity: 0.5,
          background: '#f0f4ff',
          zIndex: 1,
          position: 'relative' as const,
        }
      : {}),
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      {sortMode && (
        <td style={{ padding: '12px', textAlign: 'center', width: '50px' }}>
          <button
            {...listeners}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'grab',
              padding: '4px 8px',
              touchAction: 'none',
            }}
            title="Drag to reorder"
          >
            <FontAwesomeIcon
              icon={faGripVertical}
              style={{ fontSize: '16px', color: '#999' }}
            />
          </button>
        </td>
      )}
      <td style={{ padding: '12px' }}>
        <button
          onClick={() => onNavigateToShow(doc)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            color: '#2563eb',
            textDecoration: 'none',
          }}
        >
          <Body weight="medium" style={{ color: '#2563eb' }}>
            {doc.name}
          </Body>
        </button>
      </td>
      <td style={{ padding: '12px' }}>
        <Small>{TAX_TYPE_LABELS[doc.tax_type] || doc.tax_type}</Small>
      </td>
      <td style={{ padding: '12px' }}>
        <Small>{doc.states?.join(', ') || 'ALL'}</Small>
      </td>
      <td style={{ padding: '12px', textAlign: 'center' }}>
        {doc.active ? (
          <Chip color="success" label="Active" size="small" />
        ) : (
          <Stack gap={4} vertical style={{ alignItems: 'center' }}>
            <Chip label="Inactive" size="small" />
            {!canActivate(doc) && (
              <Small style={{ color: '#b91c1c', fontSize: '10px' }}>
                Set signature to activate
              </Small>
            )}
          </Stack>
        )}
      </td>
      <td style={{ padding: '12px', textAlign: 'center' }}>
        <Small>
          v{doc.latest_version?.version_number || 0} ({doc.versions_count})
        </Small>
      </td>
      <td style={{ padding: '12px' }}>
        <Small>{formatDate(doc.updated_at)}</Small>
      </td>
      {!sortMode && (
        <td style={{ padding: '12px', textAlign: 'right' }}>
          <Stack gap={6} justify="end">
            <button
              onClick={() => onToggleActive(doc)}
              style={{
                background: 'none',
                border: 'none',
                cursor:
                  !doc.active && !canActivate(doc) ? 'not-allowed' : 'pointer',
                padding: '4px 8px',
                opacity: !doc.active && !canActivate(doc) ? 0.4 : 1,
              }}
              title={
                !doc.active && !canActivate(doc)
                  ? 'Set signature position before activating'
                  : doc.active
                    ? 'Deactivate'
                    : 'Activate'
              }
            >
              <FontAwesomeIcon
                icon={doc.active ? faEyeSlash : faEye}
                style={{ fontSize: '14px', color: '#666' }}
              />
            </button>
            <button
              onClick={() => onEdit(doc)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
              title="Edit"
            >
              <FontAwesomeIcon
                icon={faEdit}
                style={{ fontSize: '14px', color: '#666' }}
              />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpload(doc);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
              title="Upload New Version"
            >
              <FontAwesomeIcon
                icon={faUpload}
                style={{ fontSize: '14px', color: '#666' }}
              />
            </button>
          </Stack>
        </td>
      )}
    </tr>
  );
};

const Documents: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Sort mode
  const [sortMode, setSortMode] = useState(false);
  const [sortOrder, setSortOrder] = useState<number[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);

  // Modals
  const [uploadDoc, setUploadDoc] = useState<Document | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DocumentsAPI.getDocuments();
      if (response.success) {
        setDocuments(response.data);
        setSortOrder(response.data.map((d) => d.id));
      }
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to fetch documents',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleCreateClick = () => {
    navigate(`/${ROUTES.documents}/${ROUTES.documentCreate}`);
  };

  const handleEditClick = (doc: Document) => {
    navigate(`/${ROUTES.documents}/${doc.id}/edit`);
  };

  const canActivate = (doc: Document) => {
    return doc.latest_version?.has_signature_coordinates === true;
  };

  const handleToggleActive = async (doc: Document) => {
    if (!doc.active && !canActivate(doc)) {
      setError(
        'Cannot activate document: signature position must be set on the latest version first. Edit the document to set it.',
      );
      setTimeout(() => setError(null), 5000);
      return;
    }
    try {
      await DocumentsAPI.updateDocument(doc.id, { active: !doc.active });
      setSuccess(
        `Document ${doc.active ? 'deactivated' : 'reactivated'} successfully`,
      );
      fetchDocuments();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to update document',
      );
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleEnterSortMode = () => {
    setSortMode(true);
    setSortOrder(documents.map((d) => d.id));
  };

  const handleCancelSort = () => {
    setSortMode(false);
    setSortOrder(documents.map((d) => d.id));
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      await DocumentsAPI.reorderDocuments(sortOrder);
      setSuccess('Document order saved successfully');
      setSortMode(false);
      fetchDocuments();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to save order',
      );
      setTimeout(() => setError(null), 5000);
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSortOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as number);
        const newIndex = prev.indexOf(over.id as number);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleUploadSuccess = () => {
    setUploadDoc(null);
    setSuccess('New version uploaded successfully');
    fetchDocuments();
    setTimeout(() => setSuccess(null), 3000);
  };

  const sortedDocuments = useMemo(
    () =>
      sortMode
        ? sortOrder
            .map((id) => documents.find((d) => d.id === id)!)
            .filter(Boolean)
        : documents,
    [sortMode, sortOrder, documents],
  );

  if (loading && documents.length === 0) {
    return (
      <Page title="Document Management">
        <Card>
          <LoadingState />
        </Card>
      </Page>
    );
  }

  return (
    <Page
      primaryAction={{
        a11yLabel: 'Create a new document',
        label: 'New Document',
        onAction: handleCreateClick,
      }}
      size="full"
      title="Document Management"
    >
      {error && (
        <Alert
          description={error}
          status="danger"
          title="Error"
          onDismiss={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          description={success}
          status="success"
          title="Success"
          onDismiss={() => setSuccess(null)}
        />
      )}

      <Card>
        <Card.Section>
          <Stack gap={12} justify="between">
            <Body weight="medium">
              {documents.length} document{documents.length !== 1 ? 's' : ''}
            </Body>
            <Stack gap={8}>
              {sortMode ? (
                <>
                  <Button
                    a11yLabel="Cancel sort"
                    appearance="outline"
                    label="Cancel"
                    onClick={handleCancelSort}
                  />
                  <Button
                    a11yLabel="Save order"
                    isLoading={savingOrder}
                    label="Save Order"
                    onClick={handleSaveOrder}
                  />
                </>
              ) : (
                <Button
                  a11yLabel="Sort documents"
                  appearance="outline"
                  label="Sort Documents"
                  onClick={handleEnterSortMode}
                />
              )}
            </Stack>
          </Stack>
        </Card.Section>

        <div style={{ overflowX: 'auto' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  {sortMode && (
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        width: '50px',
                      }}
                    />
                  )}
                  <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>
                    Tax Type
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>States</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>
                    Version
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>
                    Updated
                  </th>
                  {!sortMode && (
                    <th style={{ padding: '12px', textAlign: 'right' }}>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <SortableContext
                items={sortOrder}
                strategy={verticalListSortingStrategy}
                disabled={!sortMode}
              >
                <tbody>
                  {sortedDocuments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={sortMode ? 7 : 8}
                        style={{ padding: '24px', textAlign: 'center' }}
                      >
                        <Body>
                          No documents found. Create your first document to get
                          started.
                        </Body>
                      </td>
                    </tr>
                  ) : (
                    sortedDocuments.map((doc) => (
                      <SortableRow
                        key={doc.id}
                        doc={doc}
                        sortMode={sortMode}
                        canActivate={canActivate}
                        onToggleActive={handleToggleActive}
                        onEdit={handleEditClick}
                        onUpload={setUploadDoc}
                        onNavigateToShow={(d) =>
                          navigate(`/${ROUTES.documents}/${d.id}`)
                        }
                      />
                    ))
                  )}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        </div>
      </Card>

      {uploadDoc && (
        <UploadVersionModal
          document={uploadDoc}
          onClose={() => setUploadDoc(null)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </Page>
  );
};

export default Documents;
